import { Injectable } from '@angular/core';
import { ProjectService } from '../../resources/project/project.service';
import { BuildingService } from '../../resources/building/building.service';
import { AreaService } from '../../resources/area/area.service';
import { sortBy } from 'lodash-es';
import { BasicAreaService } from '../../resources/basic-area/basic-area.service';
import { ModuleService } from '../../resources/module/module.service';
import { SubmoduleService } from '../../resources/submodule/submodule.service';
import { UserService } from '../../resources/user/user.service';
import { ConstantsService } from '../constants/constants.service';
import { FilterTreeJSONItem } from '../../services/modal/modals/filters/filters.interface';
import { ModalService } from '../../services/modal/modal.service';
import { TableFiltersCoreService } from './table-filters-core.service';
import { TranslateService } from '../translate/translate.service';
import { ElementService } from '../../resources/element/element.service';

/**
 * Helper class/service for the elements table.
 *
 * Generates items for use in table filters.
 *
 * @todo Currently it returns all of a kind, for example, `getAreas()` returns *all*
 *  areas, while in some cases, you only want *some* areas, like when displaying a
 *  table for a specific cluster. Either implement a feature to select subsets, or,
 *  maybe even better, base/filter the selection on the input/display data (the table
 *  data/rows). Note that this may decrease performance. Since it's only used in the
 *  elements table, which displays 100% of the data, these filters are not needed.
 */
@Injectable({
  providedIn: 'root'
})
export class TableFilterService extends TableFiltersCoreService {

  constructor(
    private areaService: AreaService,
    private basicAreaService: BasicAreaService,
    private buildingService: BuildingService,
    private constantsService: ConstantsService,
    private elementService: ElementService,
    protected modalService: ModalService,
    private moduleService: ModuleService,
    private projectService: ProjectService,
    private submoduleService: SubmoduleService,
    private translateService: TranslateService,
    private userService: UserService
  ) {
    super(modalService);
  }

  /**
   *  Get comment filter list.
   *
   * @returns Comment filter tree item.
   */
  public getComments(): FilterTreeJSONItem {
    const comment: FilterTreeJSONItem = { label: this.translateService.getPlural('comment'), children: [] };
    comment.children.push({
      label: this.translateService.get('noComment'),
      value: 0,
      key: 'comments',
      active: true
    });

    // Count comments, and generate list.
    const elements = this.elementService.get().filter(e => e.active);
    const commentCount = [];

    // Collect all different comment counts.
    for (const e of elements) {
      if (e.commentCount && commentCount.indexOf(e.commentCount) === -1) {
        commentCount.push(e.commentCount);
      }
    }
    for (const count of commentCount.sort((a, b) => a - b)) {
      comment.children.push({
        label: `${ count } ${ count === 1
          ? this.translateService.get('comment')
          : this.translateService.getPlural('comment') }`,
        value: 1,
        key: 'comments',
        active: true
      });
    }
    return comment;
  }

  /**
   * Get area filter list.
   *
   * @returns Area filters tree item.
   */
  public getAreas(): FilterTreeJSONItem {
    const area: FilterTreeJSONItem = { label: this.translateService.get('area'), children: [] };
    const areas = this.areaService.get().filter(a => a.active);
    for (const a of sortBy(areas, 'name')) {
      area.children.push({
        label: a.name,
        value: a._id,
        key: 'constructionId',
        active: true,
      });
    }
    return area.children.length
      ? area
      : null;
  }

  /**
   * Get area template filter list.
   *
   * @returns Area template filters tree item.
   */
  public getAreaTemplates(): FilterTreeJSONItem {
    const areaTemplate: FilterTreeJSONItem = { label: this.translateService.get('areatemplate'), children: [] };
    const areaTemplates = this.basicAreaService.get().filter(at => at.active);
    for (const at of sortBy(areaTemplates, 'name')) {
      areaTemplate.children.push({
        label: at.name,
        value: at._id,
        key: 'constructionId',
        active: true,
      });
    }
    return areaTemplate.children.length
      ? areaTemplate
      : null;
  }

  /**
   * Get building filter list.
   *
   * @returns Building filters tree item.
   */
  public getBuildings(): FilterTreeJSONItem {
    const building: FilterTreeJSONItem = { label: this.translateService.get('building'), children: [] };
    const buildings = this.buildingService.get().filter(b => b.active);
    for (const b of sortBy(buildings, 'name')) {
      building.children.push({
        label: b.name,
        value: b._id,
        key: 'constructionId',
        active: true,
      });
    }
    return building.children.length
      ? building
      : null;
  }

  /**
   * Get module filter list.
   *
   * @returns Module filters tree item.
   */
  public getModules(): FilterTreeJSONItem {
    const module: FilterTreeJSONItem = { label: this.translateService.get('module'), children: [] };
    const modules = this.moduleService.get().filter(m => m.active);
    for (const m of modules) {
      module.children.push({
        label: m.name,
        value: m._id,
        key: 'moduleId',
        active: true,
      });
    }
    return module;
  }

  /**
   * Get Module/submodule tree.
   *
   * Combined list with modules as primary, submodules secundary,
   * to create a tree like presentation.
   *
   * @returns Module and submodule tree.
   */
  public getModulesWithSubmodules(): FilterTreeJSONItem[] {
    const modules = [];
    const mods = [
      ...this.moduleService.get().filter(m => m.active && !m.hide && !m.isTechnicalType),
      ...this.moduleService.get().filter(m => m.active && m.isTechnicalType)
    ];
    for (const m of mods) {
      const module: FilterTreeJSONItem = {
        label: `${ m.chapter ? m.chapter + '. ' : ''}${ m.name }`,
        children: []
      };
      for (const s of m.submodules.filter(sm => sm.active && !sm.hide)) {
        module.children.push({
          label: `${ s.chapter ? s.chapter + '. ' : ''}${ s.name }`,
          value: s._id,
          key: 'submoduleId',
          active: true,
        });
      }
      if (module.children.length) {
        modules.push(module);
      }
    }
    return modules;
  }

  /**
   * Get project filter list.
   *
   * @returns Project filters tree item.
   */
  public getProjects(): FilterTreeJSONItem {
    const project: FilterTreeJSONItem = { label: this.translateService.get('project'), children: [{
      label: this.projectService.getName(),
      value: 1,
      key: 'projectLevel',
      active: true,
    }] };
    return project;
  }

  /**
   * Get status filter list.
   *
   * @returns Status filters tree item.
   */
  public getStatuses(): FilterTreeJSONItem {
    const status: FilterTreeJSONItem = { label: this.translateService.get('status'), children: [] };
    const statuses = this.constantsService.getStatusTypes();
    for (const s of sortBy(statuses, 'displayName')) {
      status.children.push({
        label: s.displayName,
        value: s._id,
        key: 'status',
        active: true,
      });
    }
    return status;
  }

  /**
   * Get submodule filter list.
   *
   * @returns Submodule filters tree item.
   */
  public getSubmodules(): FilterTreeJSONItem {
    const submodule: FilterTreeJSONItem = { label: this.translateService.get('submodule'), children: [] };
    const submodules = this.submoduleService.get().filter(sm => sm.active);
    for (const s of submodules) {
      submodule.children.push({
        label: s.name,
        value: s._id,
        key: 'submoduleId',
        active: true,
      });
    }
    return submodule;
  }

  /**
   * Get user filter list.
   *
   * @returns User filters tree item.
   */
  public getUsers(): FilterTreeJSONItem {
    const user: FilterTreeJSONItem = { label: this.translateService.get('user'), children: [] };
    const users = this.userService.get().filter(u => u.active);
    for (const u of sortBy(users, 'name')) {
      user.children.push({
        label: u.fullName,
        value: u._id as string,
        key: 'assignedToUserId',
        active: true,
      });
    }
    return user;
  }
}
